import { supabase } from "@/integrations/supabase/client";
import { getFaviconFromUrl } from "@/utils/favicon";

const PARALLEL_BASE_URL = "https://api.parallel.ai";

// Hardcoded Parallel API key per user request
const PARALLEL_API_KEY = "r8KdUJEc4DffFvJcM8sXNOvqtzCIp9MDCGcB1TfX";

function getParallelApiKey(): string {
  return PARALLEL_API_KEY;
}

// Note: All client-side calls route through Supabase Edge Functions to avoid CORS and key exposure.

export type ParallelSearchOptions = {
  objective?: string;
  search_queries?: string[];
  processor?: "base" | "pro";
  max_results?: number;
  max_chars_per_result?: number;
  source_policy?: any;
};

export async function parallelSearch(opts: ParallelSearchOptions) {
  // Direct Supabase edge function call
  const { data, error } = await supabase.functions.invoke('parallel-search', {
    body: {
      objective: opts.objective,
      search_queries: opts.search_queries,
      processor: opts.processor || "base",
      max_results: opts.max_results ?? 10,
      max_chars_per_result: opts.max_chars_per_result ?? 6000,
      source_policy: opts.source_policy,
    }
  });

  if (error) {
    throw new Error(error.message || "Parallel Search failed");
  }

  return data;
}

export type ParallelSearchFinding = {
  index: number;
  title: string;
  url: string;
  excerpt: string;
  imageUrl?: string;
  faviconUrl?: string;
  publishedAt?: string;
};

export type FormattedSearchFindings = {
  markdown: string;
  context: string;
  entries: ParallelSearchFinding[];
};

export function formatSearchFindings(searchJson: any): FormattedSearchFindings {
  if (!searchJson || !Array.isArray(searchJson.results)) {
    return { markdown: "", context: "", entries: [] };
  }

  const entries: ParallelSearchFinding[] = searchJson.results
    .filter(Boolean)
    .slice(0, 8)
    .map((r: any, idx: number) => {
      const title: string = r.title || r.url || `Result ${idx + 1}`;
      const url: string = r.url || "";
      const rawExcerpt: string = Array.isArray(r.excerpts)
        ? r.excerpts.join(" ")
        : r.excerpt || r.snippet || r.description || "";
      const excerpt = rawExcerpt.replace(/\s+/g, " ").trim().slice(0, 600);
      const imageUrl: string | undefined = r.image_url || r.imageUrl || r.image?.url || r.thumbnail_url || undefined;
      const fallbackFavicon = getFaviconFromUrl(url);
      const faviconUrl: string | undefined =
        r.favicon_url || r.faviconUrl || (fallbackFavicon || undefined);
      const publishedAt: string | undefined = r.published_date || r.publishedAt || r.date || undefined;

      return {
        index: idx + 1,
        title,
        url,
        excerpt,
        imageUrl,
        faviconUrl,
        publishedAt,
      };
    });

  const markdown = entries
    .map((entry) => {
      const lines: string[] = [];
      const link = entry.url ? `[${entry.title}](${entry.url})` : entry.title;
      lines.push(`${entry.index}. **${link}**`);
      if (entry.publishedAt) {
        lines.push(`   _Published: ${new Date(entry.publishedAt).toLocaleDateString()}_`);
      }
      if (entry.imageUrl) {
        lines.push(`   ![${entry.title}](${entry.imageUrl})`);
      }
      if (entry.excerpt) {
        lines.push(`   > ${entry.excerpt}`);
      }
      return lines.join("\n");
    })
    .join("\n\n");

  const context = entries
    .map((entry) => {
      const parts = [`[${entry.index}] ${entry.title}`];
      if (entry.url) parts.push(entry.url);
      if (entry.excerpt) parts.push(entry.excerpt);
      return parts.join(" — ");
    })
    .join("\n\n");

  return { markdown, context, entries };
}

export type ParallelTaskRunOptions = {
  input: string | Record<string, any>;
  processor: "base" | "pro";
  enable_events?: boolean;
  source_policy?: any;
  mcp_servers?: any[];
  betas?: string[];
  metadata?: Record<string, string>;
};

export async function createTaskRun(opts: ParallelTaskRunOptions) {
  const apiKey = getParallelApiKey();
  const url = `${PARALLEL_BASE_URL}/v1/tasks/runs`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      ...(opts.betas && opts.betas.length
        ? { "parallel-beta": opts.betas.join(",") }
        : {}),
    },
    body: JSON.stringify({
      input: opts.input,
      processor: opts.processor,
      enable_events: opts.enable_events ?? true,
      source_policy: opts.source_policy,
      mcp_servers: opts.mcp_servers,
      metadata: opts.metadata,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Parallel TaskRun create failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getTaskRunResult(runId: string, betas?: string[], timeoutSec = 600) {
  const apiKey = getParallelApiKey();
  const url = `${PARALLEL_BASE_URL}/v1/tasks/runs/${encodeURIComponent(runId)}/result?timeout=${timeoutSec}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "x-api-key": apiKey,
      ...(betas && betas.length ? { "parallel-beta": betas.join(",") } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Parallel TaskRun result failed (${res.status}): ${text}`);
  }
  return res.json();
}

export type UserContext = {
  name?: string;
  location?: string;
  timezone?: string;
};

export type ParallelChatOptions = {
  messages: { role: "system" | "user" | "assistant"; content: string }[];
  model?: string; // e.g. "speed"
  temperature?: number;
  userContext?: UserContext;
  deepResearchContext?: {
    dateStr: string;
    timeStr: string;
    findingsContext: string;
    findingsEntries: ParallelSearchFinding[];
  };
};

export async function parallelChat(opts: ParallelChatOptions): Promise<string> {
  // Direct Supabase edge function call
  try {
    const { data, error } = await supabase.functions.invoke('parallel-chat', {
      body: {
        model: opts.model || "speed",
        messages: opts.messages,
        temperature: typeof opts.temperature === "number" ? opts.temperature : 0.7,
        userContext: opts.userContext,
        deepResearchContext: opts.deepResearchContext
      }
    });

    if (error) {
      throw new Error(`Parallel Chat failed: ${error.message}`);
    }

    // OpenAI-compatible payload
    const content = data?.choices?.[0]?.message?.content;
    if (typeof content === "string") return content;
    // Some proxies may return `{ content: string }` directly
    if (typeof data?.content === "string") return data.content;
    return "";
  } catch (error: any) {
    throw new Error(error.message || "Parallel Chat proxy failed");
  }
}

function sanitizeParallelContent(content: string): string {
  if (!content) {
    return "";
  }

  let sanitized = content;

  const removeSection = (heading: string, endMarkers: string[]) => {
    const headingLower = heading.toLowerCase();
    let lowerContent = sanitized.toLowerCase();

    while (lowerContent.includes(headingLower)) {
      const startIdx = lowerContent.indexOf(headingLower);
      if (startIdx === -1) break;

      let endIdx = sanitized.length;
      for (const marker of endMarkers) {
        const markerLower = marker.toLowerCase();
        const markerIdx = lowerContent.indexOf(markerLower, startIdx + headingLower.length);
        if (markerIdx !== -1) {
          endIdx = Math.min(endIdx, markerIdx);
        }
      }

      const prefix = sanitized.slice(0, startIdx).trimEnd();
      const suffix = endIdx < sanitized.length ? sanitized.slice(endIdx).trimStart() : "";
      sanitized = prefix + (prefix && suffix ? "\n\n" : "") + suffix;
      lowerContent = sanitized.toLowerCase();
    }
  };

  removeSection(
    "Summary of How to Keep Context and Answer Repeated or Follow-up Questions (Based on Research)",
    ["\n## ", "\n### ", "\nIn short", "\nAnswer", "\nKey takeaways", "\nExpected response", "\n\n"]
  );

  removeSection(
    "Example API Message Format",
    ["\n## ", "\n### ", "\nIn short", "\nAnswer", "\nExpected response", "\n\n"]
  );

  // Remove code examples that expose system prompts or API usage details.
  sanitized = sanitized.replace(/```[a-z]*[\s\S]*?completion\.choices\[0\].[\s\S]*?```/gi, "");
  sanitized = sanitized.replace(/messages\s*=\s*\$\$?[\s\S]*?response\s*=\s*completion\.choices\[0\].[\s\S]*?(?:$|\n\n)/gi, "");

  // Remove residual lines explicitly referencing system prompts or internal instructions.
  sanitized = sanitized.replace(/.*\bsystem prompt\b.*\n?/gi, "");
  sanitized = sanitized.replace(/.*\binternal (instructions?|prompts?)\b.*\n?/gi, "");

  // Remove duplicate paragraphs (paragraphs that appear more than once)
  const paragraphs = sanitized.split(/\n{2,}/);
  const seen = new Set<string>();
  const uniqueParagraphs: string[] = [];
  for (const p of paragraphs) {
    const normalized = p.trim().toLowerCase();
    // Only skip if it's a substantial paragraph (>50 chars) and already seen
    if (normalized.length > 50 && seen.has(normalized)) {
      continue;
    }
    if (normalized.length > 50) {
      seen.add(normalized);
    }
    if (p.trim()) {
      uniqueParagraphs.push(p.trim());
    }
  }
  sanitized = uniqueParagraphs.join("\n\n");

  // Remove duplicate headings with same content
  const lines = sanitized.split('\n');
  const seenHeadings = new Set<string>();
  const filteredLines: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isHeading = /^#{1,3}\s+/.test(line);
    if (isHeading) {
      const headingKey = line.toLowerCase().trim();
      if (seenHeadings.has(headingKey)) {
        // Skip duplicate heading and its immediate content until next heading
        while (i + 1 < lines.length && !/^#{1,3}\s+/.test(lines[i + 1])) {
          i++;
        }
        continue;
      }
      seenHeadings.add(headingKey);
    }
    filteredLines.push(line);
  }
  sanitized = filteredLines.join('\n');

  // Collapse any excessive newlines introduced during sanitization.
  sanitized = sanitized.replace(/\n{3,}/g, "\n\n").trim();

  return sanitized;
}

export async function deepResearchRespond(
  userQuery: string,
  nowISO: string,
  options?: { preferSearch?: boolean; processor?: "base" | "pro"; userContext?: UserContext }
): Promise<{ content: string; sources?: ParallelSearchFinding[] }> {
  const date = new Date(nowISO);
  const dateStr = date.toLocaleDateString();
  const timeStr = date.toLocaleTimeString();

  // Heuristic: use Parallel Search when query suggests web info or freshness
  const shouldSearch =
    options?.preferSearch ||
    /news|latest|recent|sources?|references?|cite|web|search|find|compare|vs\b|review\b|pricing|launch|release|rumor|is it true|fact-check/i.test(
      userQuery
    ) ||
    userQuery.length > 220;

  let findingsMarkdown = "";
  let findingsContext = "";
  let findingsEntries: ParallelSearchFinding[] = [];
  if (shouldSearch) {
    try {
      const searchJson = await parallelSearch({
        objective: userQuery,
        processor: options?.processor || "base",
        max_results: 10,
        max_chars_per_result: 6000,
      });
      const formatted = formatSearchFindings(searchJson);
      findingsMarkdown = formatted.markdown;
      findingsContext = formatted.context;
      findingsEntries = formatted.entries;
    } catch (e: any) {
      console.warn("Deep Research search failed, continuing without web findings:", e?.message || e);
    }
  }

  // Pass context to backend instead of constructing system prompt here
  const messages: ParallelChatOptions["messages"] = [
    { role: "user", content: userQuery },
  ];

  const rawContent = await parallelChat({
    messages,
    model: "speed",
    temperature: 0.5,
    userContext: options?.userContext,
    deepResearchContext: {
      dateStr,
      timeStr,
      findingsContext,
      findingsEntries
    }
  });
  const cleanedContent = sanitizeParallelContent(rawContent);

  // Return the cleaned content directly - no need to add artificial sections
  // since the AI response already has good structure
  return {
    content: cleanedContent.trim(),
    sources: findingsEntries
  };
}
